import { GridCells, Row } from 'components/GridItemList';
import PartyName from 'components/party/PartyName';
import { ReactNode } from 'react';
import { InvoiceForm } from 'types/invoices';
import { getAddress } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { yesNo } from './GeneralCard';

type InvoiceParty = InvoiceForm['clientParty'];

/**
 * A payer's or a service recipient's facts as the invoice shows them: its official name as its chip, with its party
 * number, and its own name when that differs, then its VAT liability, VAT number while it is liable, e-mail and billing
 * address, or its registration address when it has no billing one. The name takes a row of its own, with `nameAction`
 * beside the chip.
 */
export function partyFactRows(party: InvoiceParty, nameAction?: ReactNode): Row[] {
    const address = party.billingAddress ?? party.registrationAddress;
    return [
        {
            label: t('official-name'),
            // In the value, not the row's button, which the grid puts at the end of the row.
            content: (
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <PartyName party={party} isLink useOfficialName showPartyNumber />
                    {nameAction}
                </span>
            ),
            wide: true,
        },
        ...(party.officialName && party.name !== party.officialName ? [{ label: t('name'), content: party.name }] : []),
        { label: t('is-vat-liable'), content: yesNo(party.isVatLiable) },
        ...(party.isVatLiable ? [{ label: t('vat-number'), content: party.vatRegNum || '-' }] : []),
        { label: t('email'), content: party.mainEmail || '-' },
        {
            label: t(!party.billingAddress && party.registrationAddress ? 'registration-address' : 'billing-address'),
            content: address ? getAddress(address, address.countryCode) || '-' : '-',
        },
    ];
}

/**
 * A party's facts after its picker, in a section's form: read cells that flow on in the form's own grid, the name one
 * cell like the others, as the editor has no action beside it.
 */
export function PartyFactCells({ party }: { party: InvoiceParty | null | undefined }) {
    return party ? (
        <dl className="contents">
            <GridCells rows={partyFactRows(party).map((row, index) => (index === 0 ? { ...row, wide: false } : row))} />
        </dl>
    ) : null;
}
